<template>
    <DrawerAccordion
        id="contact"
        :title="titleLabel"
        coloredIcon="user-circle"
        openByDefault
    >
        <template #action>
            <CompassPlainButton
                v-if="showEditButton && !isEmpty"
                :label="translations.edit"
                :size="CompassEnums.PlainButtonSize.SMALL"
                :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                @click.stop="handleEdit"
            />
        </template>
        <div class="overview-contact-block">
            <div class="overview-contact-block__content">
                <div v-if="isEmpty" class="overview-contact-block__empty">
                    <CompassObject :size="CompassEnums.AvatarSize.SMALL" object="contact" />
                    <p
                        class="overview-contact-block__empty-description typographies-paragraph-medium-regular"
                    >
                        {{ emptyDescription }}
                    </p>
                    <CompassTooltip
                        :content="translations.disabledTooltip"
                        :disabled="!isCreateButtonDisabled"
                    >
                        <CompassButton
                            :type="CompassEnums.ButtonType.SECONDARY"
                            :size="CompassEnums.ButtonSize.SMALL"
                            :label="emptyButton"
                            class="overview-contact-block__empty-button"
                            :disabled="isCreateButtonDisabled"
                            @click="handleCreateContact"
                        />
                    </CompassTooltip>
                </div>
                <div v-else>
                    <div class="overview-contact-block__content-header">
                        <CompassTooltip
                            v-if="canLinkToContact"
                            :content="translations.seeTheFile"
                            :arrow="false"
                            placement="right-start"
                        >
                            <RouterLink v-if="contact.link" :to="contact.link">
                                <CompassObject
                                    :label="contact.fullName"
                                    :caption="contact.position"
                                    :size="CompassEnums.AvatarSize.SMALL"
                                    object="contact"
                                    isLink
                                />
                            </RouterLink>
                        </CompassTooltip>
                        <CompassObject
                            v-else
                            :label="contact.fullName"
                            :caption="contact.position"
                            :size="CompassEnums.AvatarSize.SMALL"
                            object="contact"
                        />
                    </div>
                    <CompassDescriptionList
                        :variant="CompassEnums.DescriptionListVariantType.FROZEN_FIELD"
                        :size="CompassEnums.DescriptionListSize.SMALL"
                    >
                        <CompassDescriptionListItem
                            v-if="contact.email"
                            :label="translations.email"
                        >
                            <EmailLinkCompass
                                :email="contact.email"
                                :linkedid="linkedid"
                                :linkedtype="linkedtype"
                            >
                                {{ contact.email }}
                            </EmailLinkCompass>
                        </CompassDescriptionListItem>
                        <CompassDescriptionListItem
                            v-if="contact.phone"
                            :label="translations.phone"
                        >
                            <PhoneLinkCompass :phoneNumber="contact.phone" />
                        </CompassDescriptionListItem>
                        <CompassDescriptionListItem
                            v-if="contact.mobile"
                            :label="translations.mobile"
                        >
                            <PhoneLinkCompass :phoneNumber="contact.mobile" />
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                </div>
            </div>
            <CompassPlainButton
                v-if="showAllContacts"
                class="overview-contact-block__all-contacts"
                :label="allContactsLabel"
                @click="navigate"
            />
        </div>
    </DrawerAccordion>
</template>
<script setup lang="ts">
import { computed, inject } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { useStore } from 'vuex';
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import { arrayToObject } from 'Helpers/arrayToObjectHelper.js';
import type { Translator } from 'js/shims-vue';

type LinkedType = 'third' | 'opportunity' | 'people';

interface Contact {
    peopleId?: number;
    fullName?: string;
    position?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    link?: RouteLocationRaw;
}

interface ContactsCountInfo {
    key: string;
    value: number;
}

interface Acl {
    update_opportunities?: boolean;
    update_contacts?: boolean;
}

interface Permissions {
    acl: Acl;
}

interface PeopleLegacy {
    edit(context: string, peopleId?: number): void;
    linkTo(type: string, options: { id: number }): void;
}

interface Props {
    linkedtype: LinkedType;
    linkedid: number;
}

const props = defineProps<Props>();

const $t = inject('$t') as Translator;

const store = useStore();
const router = useRouter();
const route = useRoute();

const translations = {
    edit: $t.__('common.actions.edit'),
    disabledTooltip: $t.__('overview.contactBlock.empty.disabledTooltip'),
    seeTheFile: $t.__('common.actions.see_the_file'),
    email: $t.__('common.labels.email'),
    phone: $t.__('common.labels.phone'),
    mobile: $t.__('common.labels.mobile'),
    titlesByType: {
        third: $t.__('overview.contactBlock.third.title'),
        people: $t.__('overview.contactBlock.people.title'),
        opportunity: $t.__('overview.contactBlock.opportunity.title'),
    } satisfies Record<LinkedType, string>,
    emptyDescriptionsByType: {
        third: $t.__('overview.contactBlock.empty.third.description'),
        people: $t.__('overview.contactBlock.empty.third.description'),
        opportunity: $t.__('overview.contactBlock.empty.opportunity.description'),
    } satisfies Record<LinkedType, string>,
    emptyButtonsByType: {
        third: $t.__('overview.contactBlock.empty.third.button'),
        people: $t.__('overview.contactBlock.empty.third.button'),
        opportunity: $t.__('overview.contactBlock.empty.opportunity.button'),
    } satisfies Record<LinkedType, string>,
    allContactsLabelsByType: {
        third: $t.__('overview.contactBlock.third.allContacts'),
        people: $t.__('overview.contactBlock.third.allContacts'),
        opportunity: $t.__('overview.contactBlock.opportunity.allContacts'),
    } satisfies Record<LinkedType, string>,
};

const permissions = computed<Permissions>(() => store.getters['user/getPermissions']);

const isOpportunity = computed<boolean>(() => props.linkedtype === 'opportunity');
const isPeople = computed<boolean>(() => props.linkedtype === 'people');

const titleLabel = computed<string>(() => translations.titlesByType[props.linkedtype]);

const contact = computed<Contact>(() => {
    if (props.linkedtype === 'third') {
        return store.getters.mainContact ?? {};
    }
    if (isOpportunity.value) {
        return arrayToObject(store.getters.mainContact);
    }
    if (isPeople.value) {
        const identity = store.state.people.identity ?? {};
        const infos = arrayToObject(store.state.people.dataFetch.informations);
        return {
            peopleId: identity.id,
            fullName: identity.name,
            position: infos.position,
            email: identity.email,
            phone: infos.phone,
            mobile: identity.mobile,
            link: identity.link,
        };
    }
    return {};
});

const isEmpty = computed<boolean>(() => Object.keys(contact.value).length === 0);

const canLinkToContact = computed<boolean>(() => !isPeople.value && Boolean(contact.value.link));

const showEditButton = computed<boolean>(() => {
    const acl = permissions.value.acl;
    return Boolean(isOpportunity.value ? acl.update_opportunities : acl.update_contacts);
});

const emptyDescription = computed<string>(
    () => translations.emptyDescriptionsByType[props.linkedtype],
);

const emptyButton = computed<string>(() => translations.emptyButtonsByType[props.linkedtype]);

const isCreateButtonDisabled = computed<boolean>(() =>
    isOpportunity.value ? !permissions.value.acl.update_opportunities : false,
);

const showAllContacts = computed<boolean>(() => {
    if (isPeople.value) {
        return false;
    }
    if (isOpportunity.value) {
        return (store.state.opportunity.mainContact ?? []).length > 1;
    }
    const companyInfo: ContactsCountInfo[] = store.state.third.dataFetch.informations ?? [];
    const contactsCount = companyInfo.find((item) => item.key === 'contactsCount');
    return (contactsCount?.value ?? 0) > 1;
});

const allContactsLabel = computed<string>(
    () => translations.allContactsLabelsByType[props.linkedtype],
);

const navigate = (): void => {
    const name = isOpportunity.value
        ? 'opportunity.overview.informations'
        : 'third.overview.contacts';
    router.push({ name, query: route.query });
};

const handleEdit = (): void => {
    let context = 'thirdOverview';
    if (isOpportunity.value) {
        context = 'opportunityOverview';
    } else if (isPeople.value) {
        context = 'peopleOverview';
    }
    (People as PeopleLegacy).edit(context, contact.value.peopleId);
};

const handleCreateContact = (): void => {
    if (isOpportunity.value) {
        store.dispatch('popupPanel/open', {
            key: 'opportunityForm',
            props: {
                oppId: props.linkedid,
                emitEditSuccess: 'opportunity:update',
                context: 'overview',
            },
        });
        return;
    }
    (People as PeopleLegacy).linkTo('third', { id: props.linkedid });
};
</script>
<style lang="scss" scoped>
.overview-contact-block {
    &__empty {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: var(--spacing-1);
        &-description {
            margin: var(--spacing-1) 0;
        }
    }
    &__content-header {
        margin: 0 0 var(--spacing-1);
    }
    &__all-contacts {
        display: inline-block;
        margin-top: var(--spacing-1);
    }
}
.overview-contact-block__content :deep(.compass-object),
.overview-contact-block__content :deep(.compass-object__text) {
    min-width: 0;
}
.overview-contact-block__content :deep(.compass-object__caption),
.overview-contact-block__content :deep(.compass-object__label) {
    overflow: hidden;
    text-overflow: ellipsis;
}
.overview-contact-block__content :deep(.compass-object__label) {
    white-space: nowrap;
}
.overview-contact-block__content :deep(.shareUrlBtn) {
    font-weight: var(--font-weights-lato-1);
}
.overview-contact-block__content :deep(.compass-icon-button) {
    flex-shrink: 0;
}
.overview-contact-block__content :deep(.compass-object--small .compass-object__label) {
    line-height: var(--line-heights-20);
}
.overview-contact-block__content :deep(.compass-object--small .compass-object__caption) {
    line-height: var(--line-heights-16);
}
.overview-contact-block__content :deep(.compass-description-list__item) {
    min-width: 0; // stops overflow
}
.overview-contact-block__content :deep(.compass-description-list__item__content) {
    min-height: var(--line-heights-20);
}
.overview-contact-block__content :deep(.copy-element) {
    display: block;
    max-width: 100%;
}
.overview-contact-block__content :deep(.compass-link) {
    max-width: calc(100% - 24px);
}
.overview-contact-block__content :deep(.compass-link__label) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}
.overview-contact-block__all-contacts:deep(.compass-plain-button) {
    color: var(--colors-common-text-subtle-default);
    &:hover {
        color: var(--colors-common-text-subtle-hover);
    }
}
</style>
